// TZ-safe helpers for displaying/comparing `TrainingSlot.date`.
//
// `slot.date` is stored as local midnight in Europe/Bratislava, serialized
// to UTC (e.g. a slot on 10 July 2026 is stored as "2026-07-09T22:00:00Z"
// during CEST). Reading it back with `new Date(slot.date)` + the browser's
// *local* getters (`getDate()`, `getDay()`, `setHours()`, ...) only recovers
// the intended calendar day when the browser's own timezone happens to be
// Europe/Bratislava — anyone opening the app from another timezone sees
// events shifted onto the wrong day (or, worse, "past" events that are
// still upcoming). These helpers always resolve the calendar day/instant
// via Europe/Bratislava explicitly, so they're correct in every browser TZ.
import { format } from 'date-fns';

const EVENT_TIMEZONE = 'Europe/Bratislava';

const zonedPartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: EVENT_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second: number;
}

function getZonedParts(date: Date): ZonedParts {
  const parts = zonedPartsFormatter.formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    // Some ICU implementations render midnight as "24" with hour12: false.
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  };
}

/**
 * Converts a "wall clock" time as observed in Europe/Bratislava into the
 * actual UTC instant, correctly accounting for CET/CEST. Standard
 * guess-and-correct approach: interpret the wall time as if it were UTC,
 * see what Europe/Bratislava clock that instant maps to, then shift by the
 * difference (one iteration is sufficient outside the ~1h DST-transition
 * window, which is an acceptable precision trade-off here).
 */
function zonedWallTimeToUtcMs(year: number, month: number, day: number, hour: number, minute: number): number {
  const guessUtcMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  const seen = getZonedParts(new Date(guessUtcMs));
  const seenAsUtcMs = Date.UTC(seen.year, seen.month - 1, seen.day, seen.hour, seen.minute, 0);
  const offsetMs = seenAsUtcMs - guessUtcMs;
  return guessUtcMs - offsetMs;
}

/** Returns the calendar day (`YYYY-MM-DD`) `dateIso` falls on in Europe/Bratislava. */
export function eventDayKey(dateIso: string): string {
  const { year, month, day } = getZonedParts(new Date(dateIso));
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Returns a synthetic `Date` set to local midnight on the Europe/Bratislava
 * calendar day `dateIso` falls on. Safe to pass to date-fns `format()` or to
 * read with local getters (`getDay()`, `getDate()`, ...) — the wall-clock
 * fields already equal the Bratislava day, so those reads are correct
 * regardless of the browser's own timezone. Do NOT convert it back through
 * `toISOString()` / compare it against another real instant — it's a display
 * stand-in, not the real moment in time.
 */
export function eventLocalDate(dateIso: string): Date {
  const { year, month, day } = getZonedParts(new Date(dateIso));
  return new Date(year, month - 1, day);
}

/** Formats `dateIso`'s Europe/Bratislava calendar day with a date-fns pattern (default `'d MMM'`). */
export function formatEventDate(dateIso: string, formatStr: string = 'd MMM'): string {
  return format(eventLocalDate(dateIso), formatStr);
}

/**
 * Formats a plain `YYYY-MM-DD` calendar day — the shape the API returns for a
 * cancelled ride, already resolved to Europe/Bratislava server-side — with a
 * date-fns pattern. Reads the parts literally instead of going through
 * `new Date('2026-08-12')`, which the spec parses as UTC midnight and whose
 * local getters then land on 11 August in any behind-UTC browser. Returns the
 * input unchanged if it isn't a calendar day.
 */
export function formatEventDayKey(dayKey: string, formatStr: string = 'd. M. yyyy'): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  if (!parts) return dayKey;
  const [, year, month, day] = parts;
  return format(new Date(Number(year), Number(month) - 1, Number(day)), formatStr);
}

/**
 * True once the event has started/passed. When `startTime` ("HH:mm", read
 * as Europe/Bratislava local time — same convention as `TrainingSlot.startTime`)
 * is given, compares against that exact instant; otherwise treats the whole
 * Bratislava calendar day as "not past" until its last minute (23:59).
 */
export function isEventDayPast(dateIso: string, startTime?: string): boolean {
  const { year, month, day } = getZonedParts(new Date(dateIso));
  let hour = 23;
  let minute = 59;
  if (startTime) {
    const [h, m] = startTime.split(':').map(Number);
    if (Number.isFinite(h)) hour = h;
    if (Number.isFinite(m)) minute = m;
  }
  const eventUtcMs = zonedWallTimeToUtcMs(year, month, day, hour, minute);
  return eventUtcMs <= Date.now();
}
